import React, { useState, useEffect } from 'react';
import './App.css';

const App = () => {
  const [portCount, setPortCount] = useState('8');
  const [dataVlan, setDataVlan] = useState('1');
  const [hostname, setHostname] = useState('');
  const [defaultGateway, setDefaultGateway] = useState('');
  const [domainName, setDomainName] = useState('');
  const [dnsServer, setDnsServer] = useState('');
  const [voiceVlanId, setVoiceVlanId] = useState('');
  const [voiceVlanAutoTriggered, setVoiceVlanAutoTriggered] = useState(false);
  const [voiceVlanAutoEnabled, setVoiceVlanAutoEnabled] = useState(false);
  const [lockToVoiceData, setLockToVoiceData] = useState(false);
  const [ipRouting, setIpRouting] = useState(false);
  const [ports, setPorts] = useState([]);
  const [vlans, setVlans] = useState([]);
  const [vlanInterfaces, setVlanInterfaces] = useState([{ id: '1', ip: '', mask: '', name: '' }]);
  const [staticRoutes, setStaticRoutes] = useState([]);
  const [useDefaults, setUseDefaults] = useState(true);
  const [sshWebTimeout, setSshWebTimeout] = useState(true);
  const [passwordsNeverExpire, setPasswordsNeverExpire] = useState(true);
  const [sntpDst, setSntpDst] = useState(true);
  const [sntpServer, setSntpServer] = useState('pool.ntp.org');
  const [dstTimezone, setDstTimezone] = useState('EST -5');
  const [enableSsh, setEnableSsh] = useState(true);
  const [sshPasswordAuth, setSshPasswordAuth] = useState(true);
  const [eeeDisabled, setEeeDisabled] = useState(true);
  const [pnpDisabled, setPnpDisabled] = useState(true);
  const [commands, setCommands] = useState('');
  const [configSummary, setConfigSummary] = useState('');

  useEffect(() => {
    const count = parseInt(portCount) || 0;
    const newPorts = Array.from({ length: count }, (_, i) => ({
      id: `gi${i + 1}`,
      mode: voiceVlanId ? 'trunk' : 'access',
      untaggedVlan: dataVlan,
      taggedVlans: lockToVoiceData && voiceVlanId ? voiceVlanId : '',
      description: ''
    }));
    setPorts(newPorts);

    if (voiceVlanId && !voiceVlanAutoEnabled && !voiceVlanAutoTriggered) {
      setVoiceVlanAutoEnabled(true);
    }
  }, [portCount, dataVlan, voiceVlanId, voiceVlanAutoEnabled, voiceVlanAutoTriggered, lockToVoiceData]);

  useEffect(() => {
    const config = ['configure terminal'];

    if (hostname) config.push(`hostname ${hostname}`);
    if (domainName) config.push(`ip domain name ${domainName}`);
    if (dnsServer) config.push(`ip name-server ${dnsServer}`);

    const nonInterfaceVlans = vlans.filter(vlan => !vlanInterfaces.some(int => int.id === vlan.id));
    if (nonInterfaceVlans.length > 0) {
      config.push('vlan database');
      nonInterfaceVlans.forEach(vlan => {
        if (vlan.id) {
          config.push(vlan.name ? `vlan ${vlan.id} name ${vlan.name}` : `vlan ${vlan.id}`);
        }
      });
      config.push('exit');
    }

    vlanInterfaces.forEach(vlanInt => {
      if (vlanInt.id && vlanInt.ip && vlanInt.mask) {
        config.push(`interface vlan ${vlanInt.id}`);
        if (vlanInt.name) {
          config.push(vlanInt.name.includes(' ') ? `name "${vlanInt.name}"` : `name ${vlanInt.name}`);
        }
        config.push(`ip address ${vlanInt.ip} ${vlanInt.mask}`);
        config.push('no shutdown');
        config.push('exit');
      }
    });

    if (defaultGateway) config.push(`ip default-gateway ${defaultGateway}`);

    staticRoutes.forEach(route => {
      if (route.dest && route.mask && route.nextHop) {
        config.push(`ip route ${route.dest} ${route.mask} ${route.nextHop}`);
      }
    });

    ports.forEach(port => {
      const isDefaultAccess = port.mode === 'access' && port.untaggedVlan === '1' && !port.taggedVlans;
      if (!isDefaultAccess || port.description || port.mode === 'trunk') {
        config.push(`interface ${port.id}`);
        if (port.description) config.push(`description ${port.description}`);
        if (port.mode === 'access' && port.untaggedVlan !== '1') {
          config.push('switchport mode access');
          config.push(`switchport access vlan ${port.untaggedVlan}`);
        } else if (port.mode === 'trunk') {
          config.push('switchport mode trunk');
          if (port.untaggedVlan !== '1') config.push(`switchport trunk native vlan ${port.untaggedVlan}`);
          if (port.taggedVlans && lockToVoiceData) {
            const allowedVlans = voiceVlanId && port.untaggedVlan !== voiceVlanId
              ? `${port.untaggedVlan},${port.taggedVlans}`
              : port.taggedVlans;
            config.push(`switchport trunk allowed vlan ${allowedVlans}`);
          }
        }
        config.push('exit');
      }
    });

    if (useDefaults) {
      if (sshWebTimeout) {
        config.push('line ssh');
        config.push('exec-timeout 30');
        config.push('exit');
        config.push('ip http timeout-policy 1800 https-only');
      }
      if (passwordsNeverExpire) config.push('passwords aging 0');
      if (sntpDst) {
        config.push(`sntp server ${sntpServer}`);
        config.push(`clock timezone ${dstTimezone}`);
        config.push('clock summer-time web recurring usa');
        config.push('sntp unicast client enable');
      }
      if (enableSsh) {
        config.push('ip ssh server');
        if (sshPasswordAuth) config.push('ip ssh password-auth');
      }
      if (eeeDisabled) config.push('no eee enable');
      if (pnpDisabled) config.push('no pnp enable');
    }

    if (ipRouting) config.push('ip routing');

    if (voiceVlanId) {
      if (voiceVlanAutoTriggered) config.push('voice vlan state auto-triggered');
      if (voiceVlanAutoEnabled) config.push('voice vlan state auto-enabled');
      config.push(`voice vlan id ${voiceVlanId}`);
    }

    config.push('end');
    config.push('write memory');
    setCommands(config.join('\n'));

    const summary = [];

    summary.push('Basic Configuration:');
    summary.push(`- Hostname: ${hostname || 'Not configured'}`);
    summary.push(`- Default Gateway: ${defaultGateway || 'Not configured'}`);
    summary.push(`- Voice VLAN: ${voiceVlanId || 'Not configured'}`);
    summary.push(`- IP Routing: ${ipRouting ? 'Enabled' : 'Disabled'}`);

    const accessGroups = {};
    ports.forEach(port => {
      if (port.mode === 'access') {
        const key = port.untaggedVlan;
        if (!accessGroups[key]) accessGroups[key] = [];
        accessGroups[key].push(port);
      }
    });
    summary.push('\nAccess Port Groups:');
    Object.entries(accessGroups).forEach(([vlan, portList]) => {
      const portsWithDesc = portList.filter(p => p.description);
      const line = `- VLAN ${vlan}: ${portList.map(p => p.id).join(', ')}` +
        (portsWithDesc.length ? ` (Descriptions: ${portsWithDesc.map(p => `${p.id}: ${p.description}`).join(', ')})` : '');
      summary.push(line);
    });

    const trunks = ports.filter(p => p.mode === 'trunk');
    if (trunks.length) {
      summary.push('\nTrunk Ports:');
      trunks.forEach(port => {
        const tags = lockToVoiceData && port.taggedVlans ? port.taggedVlans : 'All VLANs Allowed';
        summary.push(`- ${port.id}: Native VLAN ${port.untaggedVlan}, Allowed VLANs: ${tags}` +
          (port.description ? ` (Description: ${port.description})` : ''));
      });
    }

    const vlan1Int = vlanInterfaces.find(v => v.id === '1');
    summary.push('\nVLAN 1 IP Address:');
    summary.push(vlan1Int && vlan1Int.ip && vlan1Int.mask ? `${vlan1Int.ip} ${vlan1Int.mask}` : 'Not configured');

    if (staticRoutes.length) {
      summary.push('\nStatic Routes:');
      staticRoutes.forEach(route => {
        if (route.dest && route.mask && route.nextHop) {
          summary.push(`- ${route.dest} ${route.mask} via ${route.nextHop}`);
        }
      });
    }

    setConfigSummary(summary.join('\n'));
  }, [
    hostname, defaultGateway, domainName, dnsServer, voiceVlanId,
    voiceVlanAutoTriggered, voiceVlanAutoEnabled, lockToVoiceData, ipRouting,
    ports, vlans, vlanInterfaces, staticRoutes,
    useDefaults, sshWebTimeout, passwordsNeverExpire, sntpDst, sntpServer, dstTimezone,
    enableSsh, sshPasswordAuth, eeeDisabled, pnpDisabled
  ]);

  const updatePort = (index, field, value) => {
    const newPorts = [...ports];
    newPorts[index][field] = value;
    setPorts(newPorts);
  };

  const addVlan = () => setVlans([...vlans, { id: '', name: '' }]);
  const updateVlan = (index, field, value) => {
    const newVlans = [...vlans];
    newVlans[index][field] = value;
    setVlans(newVlans);
  };
  const deleteVlan = (index) => {
    if (vlans.length > 0 && vlans[index].id !== voiceVlanId) {
      setVlans(vlans.filter((_, i) => i !== index));
    }
  };

  const addVlanInterface = () => setVlanInterfaces([...vlanInterfaces, { id: '', ip: '', mask: '', name: '' }]);
  const updateVlanInterface = (index, field, value) => {
    const newVlanInterfaces = [...vlanInterfaces];
    newVlanInterfaces[index][field] = value;
    setVlanInterfaces(newVlanInterfaces);
  };
  const deleteVlanInterface = (index) => {
    if (vlanInterfaces.length > 1) setVlanInterfaces(vlanInterfaces.filter((_, i) => i !== index));
  };

  const addStaticRoute = () => setStaticRoutes([...staticRoutes, { dest: '', mask: '', nextHop: '' }]);
  const updateStaticRoute = (index, field, value) => {
    const newStaticRoutes = [...staticRoutes];
    newStaticRoutes[index][field] = value;
    setStaticRoutes(newStaticRoutes);
  };
  const deleteStaticRoute = (index) => {
    if (staticRoutes.length > 0) setStaticRoutes(staticRoutes.filter((_, i) => i !== index));
  };

  const handleLockToVoiceData = () => {
    setLockToVoiceData(true);
    const updatedPorts = ports.map(port => ({
      ...port,
      taggedVlans: voiceVlanId || ''
    }));
    setPorts(updatedPorts);
  };

  const handleVoiceVlanChange = (e) => {
    const newVoiceVlanId = e.target.value;
    setVoiceVlanId(newVoiceVlanId);

    if (newVoiceVlanId && !vlans.some(vlan => vlan.id === newVoiceVlanId)) {
      setVlans([...vlans.filter(vlan => vlan.id !== voiceVlanId), { id: newVoiceVlanId, name: 'Voice' }]);
    } else if (!newVoiceVlanId) {
      setVoiceVlanAutoTriggered(false);
      setVoiceVlanAutoEnabled(false);
      setLockToVoiceData(false);
    }
  };

  const exportConfig = () => {
    const config = {
      portCount,
      dataVlan,
      hostname,
      defaultGateway,
      domainName,
      dnsServer,
      voiceVlanId,
      voiceVlanAutoTriggered,
      voiceVlanAutoEnabled,
      lockToVoiceData,
      ipRouting,
      ports,
      vlans,
      vlanInterfaces,
      staticRoutes,
      useDefaults,
      sshWebTimeout,
      passwordsNeverExpire,
      sntpDst,
      sntpServer,
      dstTimezone,
      enableSsh,
      sshPasswordAuth,
      eeeDisabled,
      pnpDisabled
    };
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${hostname || 'switch'}-config.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importConfig = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const config = JSON.parse(event.target.result);
        setPortCount(config.portCount || '8');
        setDataVlan(config.dataVlan || '1');
        setHostname(config.hostname || '');
        setDefaultGateway(config.defaultGateway || '');
        setDomainName(config.domainName || '');
        setDnsServer(config.dnsServer || '');
        setVoiceVlanId(config.voiceVlanId || '');
        setVoiceVlanAutoTriggered(config.voiceVlanAutoTriggered || false);
        setVoiceVlanAutoEnabled(config.voiceVlanAutoEnabled || false);
        setLockToVoiceData(config.lockToVoiceData || false);
        setIpRouting(config.ipRouting || false);
        setPorts(config.ports || []);
        setVlans(config.vlans || []);
        setVlanInterfaces(config.vlanInterfaces || [{ id: '1', ip: '', mask: '', name: '' }]);
        setStaticRoutes(config.staticRoutes || []);
        setUseDefaults(config.useDefaults !== undefined ? config.useDefaults : true);
        setSshWebTimeout(config.sshWebTimeout !== undefined ? config.sshWebTimeout : true);
        setPasswordsNeverExpire(config.passwordsNeverExpire !== undefined ? config.passwordsNeverExpire : true);
        setSntpDst(config.sntpDst !== undefined ? config.sntpDst : true);
        setSntpServer(config.sntpServer || 'pool.ntp.org');
        setDstTimezone(config.dstTimezone || 'EST -5');
        setEnableSsh(config.enableSsh !== undefined ? config.enableSsh : true);
        setSshPasswordAuth(config.sshPasswordAuth !== undefined ? config.sshPasswordAuth : true);
        setEeeDisabled(config.eeeDisabled !== undefined ? config.eeeDisabled : true);
        setPnpDisabled(config.pnpDisabled !== undefined ? config.pnpDisabled : true);
      } catch (error) {
        alert('Invalid config file: ' + error.message);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="App">
      <h1>Config Utility for Cisco SG/CBS/C1300 Switches</h1>
      <p>
        <a
          href="https://www.cisco.com/c/en/us/support/switches/catalyst-1300-series-switches/series.html#~tab-downloads"
          target="_blank"
          rel="noopener noreferrer"
        >
          C1300 Firmware
        </a>
      </p>
      <div className="container">
        <div className="left-column">
          <section>
            <button onClick={exportConfig}>Export Config</button>
            <label>
              <input type="file" accept=".json" onChange={importConfig} style={{ display: 'none' }} />
              <button onClick={(e) => e.target.previousSibling.click()}>Import Config</button>
            </label>
          </section>

          <section>
            <label>Hostname:</label>
            <input value={hostname} onChange={(e) => setHostname(e.target.value)} />
          </section>

          <section>
            <label>Default Gateway:</label>
            <input value={defaultGateway} onChange={(e) => setDefaultGateway(e.target.value)} />
          </section>

          <section>
            <label>Domain Name:</label>
            <input value={domainName} onChange={(e) => setDomainName(e.target.value)} />
          </section>

          <section>
            <label>DNS Server:</label>
            <input value={dnsServer} onChange={(e) => setDnsServer(e.target.value)} />
          </section>

          <section>
            <label>Voice VLAN ID:</label>
            <input value={voiceVlanId} onChange={handleVoiceVlanChange} />
            {voiceVlanId && (
              <div className="voice-vlan-options">
                <label>
                  <input
                    type="checkbox"
                    checked={voiceVlanAutoTriggered}
                    onChange={(e) => setVoiceVlanAutoTriggered(e.target.checked)}
                  />
                  Auto-Triggered
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={voiceVlanAutoEnabled}
                    onChange={(e) => setVoiceVlanAutoEnabled(e.target.checked)}
                  />
                  Auto-Enabled
                </label>
                <button onClick={handleLockToVoiceData}>Lock to Voice/Data</button>
              </div>
            )}
          </section>

          <section>
            <label>Number of Ports:</label>
            <input type="text" value={portCount} onChange={(e) => setPortCount(e.target.value)} />
          </section>

          <section>
            <label>Data Access VLAN:</label>
            <input value={dataVlan} onChange={(e) => setDataVlan(e.target.value)} />
          </section>

          <section>
            <label>
              <input
                type="checkbox"
                checked={useDefaults}
                onChange={(e) => setUseDefaults(e.target.checked)}
              />
              Use Defaults
            </label>
            {useDefaults && (
              <div className="defaults">
                <label>
                  <input
                    type="checkbox"
                    checked={sshWebTimeout}
                    onChange={(e) => setSshWebTimeout(e.target.checked)}
                  />
                  SSH/Web Timeout to 30 Mins
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={passwordsNeverExpire}
                    onChange={(e) => setPasswordsNeverExpire(e.target.checked)}
                  />
                  Set Passwords to Never Expire
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={sntpDst}
                    onChange={(e) => setSntpDst(e.target.checked)}
                  />
                  SNTP / DST
                </label>
                {sntpDst && (
                  <div className="sntp-options">
                    <label>SNTP Server:</label>
                    <input value={sntpServer} onChange={(e) => setSntpServer(e.target.value)} />
                    <label>DST Timezone:</label>
                    <select value={dstTimezone} onChange={(e) => setDstTimezone(e.target.value)}>
                      <option value="EST -5">EST</option>
                      <option value="CST -6">CST</option>
                      <option value="MST -7">MST</option>
                      <option value="PST -8">PST</option>
                    </select>
                  </div>
                )}
                <label>
                  <input
                    type="checkbox"
                    checked={enableSsh}
                    onChange={(e) => setEnableSsh(e.target.checked)}
                  />
                  Enable SSH
                </label>
                {enableSsh && (
                  <label className="sub-option">
                    <input
                      type="checkbox"
                      checked={sshPasswordAuth}
                      onChange={(e) => setSshPasswordAuth(e.target.checked)}
                    />
                    Password Auth
                  </label>
                )}
                <label>
                  <input
                    type="checkbox"
                    checked={eeeDisabled}
                    onChange={(e) => setEeeDisabled(e.target.checked)}
                  />
                  EEE Disabled
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={pnpDisabled}
                    onChange={(e) => setPnpDisabled(e.target.checked)}
                  />
                  PNP Disabled
                </label>
              </div>
            )}
          </section>

          <section>
            <h2>VLAN Configuration</h2>
            {vlans.map((vlan, index) => (
              <div key={index} className="vlan">
                <input
                  placeholder="VLAN ID"
                  value={vlan.id}
                  onChange={(e) => updateVlan(index, 'id', e.target.value)}
                />
                <input
                  placeholder="Name"
                  value={vlan.name}
                  onChange={(e) => updateVlan(index, 'name', e.target.value)}
                />
                <button onClick={() => deleteVlan(index)} disabled={vlan.id === voiceVlanId}>Delete</button>
              </div>
            ))}
            <button onClick={addVlan}>Add VLAN</button>
          </section>

          <section>
            <h2>VLAN Interfaces (L3)</h2>
            {vlanInterfaces.map((vlanInt, index) => (
              <div key={index} className="vlan-int">
                <input
                  placeholder="VLAN ID"
                  value={vlanInt.id}
                  onChange={(e) => updateVlanInterface(index, 'id', e.target.value)}
                />
                <input
                  placeholder="Name"
                  value={vlanInt.name}
                  onChange={(e) => updateVlanInterface(index, 'name', e.target.value)}
                />
                <input
                  placeholder="IP Address"
                  value={vlanInt.ip}
                  onChange={(e) => updateVlanInterface(index, 'ip', e.target.value)}
                />
                <input
                  placeholder="Subnet Mask"
                  value={vlanInt.mask}
                  onChange={(e) => updateVlanInterface(index, 'mask', e.target.value)}
                />
                <button onClick={() => deleteVlanInterface(index)}>Delete</button>
              </div>
            ))}
            <button onClick={addVlanInterface}>Add VLAN Interface</button>
            <label>
              <input
                type="checkbox"
                checked={ipRouting}
                onChange={(e) => setIpRouting(e.target.checked)}
              />
              IP Routing
            </label>
          </section>

          <section>
            <h2>Static Routes</h2>
            {staticRoutes.map((route, index) => (
              <div key={index} className="route">
                <input
                  placeholder="Destination Network"
                  value={route.dest}
                  onChange={(e) => updateStaticRoute(index, 'dest', e.target.value)}
                />
                <input
                  placeholder="Subnet Mask"
                  value={route.mask}
                  onChange={(e) => updateStaticRoute(index, 'mask', e.target.value)}
                />
                <input
                  placeholder="Next Hop"
                  value={route.nextHop}
                  onChange={(e) => updateStaticRoute(index, 'nextHop', e.target.value)}
                />
                <button onClick={() => deleteStaticRoute(index)}>Delete</button>
              </div>
            ))}
            <button onClick={addStaticRoute}>Add Static Route</button>
          </section>
        </div>

        <div className="right-column">
          <section>
            <h2>Port Configuration</h2>
            {ports.map((port, index) => (
              <div key={index} className="port">
                <input
                  value={port.id}
                  onChange={(e) => updatePort(index, 'id', e.target.value)}
                  style={{ width: '80px' }}
                />
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name={`mode-${index}`}
                      value="access"
                      checked={port.mode === 'access'}
                      onChange={(e) => updatePort(index, 'mode', e.target.value)}
                    />
                    Access
                  </label>
                  <label>
                    <input
                      type="radio"
                      name={`mode-${index}`}
                      value="trunk"
                      checked={port.mode === 'trunk'}
                      onChange={(e) => updatePort(index, 'mode', e.target.value)}
                    />
                    Trunk
                  </label>
                </div>
                <input
                  placeholder="Untagged VLAN"
                  value={port.untaggedVlan}
                  onChange={(e) => updatePort(index, 'untaggedVlan', e.target.value)}
                  style={{ width: '40px' }}
                />
                {port.mode === 'trunk' && (
                  <input
                    placeholder="Tagged VLANs"
                    value={port.taggedVlans}
                    onChange={(e) => updatePort(index, 'taggedVlans', e.target.value)}
                  />
                )}
                <input
                  placeholder="Description"
                  value={port.description}
                  onChange={(e) => updatePort(index, 'description', e.target.value)}
                />
              </div>
            ))}
          </section>
        </div>
      </div>

      <div className="bottom-container">
        <section className="commands-section">
          <h2>Proposed Commands</h2>
          <textarea value={commands} readOnly rows={30} cols={50} />
        </section>
        <section className="summary-section">
          <h2>Configuration Details</h2>
          <textarea value={configSummary} readOnly rows={30} cols={100} />
        </section>
      </div>
    </div>
  );
};

export default App;